import { supabase } from './supabaseClient';
import { fetchWithRetry } from './httpClient';
import { registerCatalogFormats } from '../utils/printFormats';

const buildApiBaseUrl = () => {
  const configured = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const trimmed = configured.replace(/\/$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};

const buildHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error('Session invalide. Merci de vous reconnecter.');
  }

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
};

const parseJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch (_error) {
    return {};
  }
};

// Timeout + seconde tentative sur reveil d'instance : voir httpClient.js.
const request = async (path, options = {}) => {
  const headers = await buildHeaders();
  const response = await fetchWithRetry(`${buildApiBaseUrl()}${path}`, {
    ...options,
    headers: {
      ...headers,
      ...(options.headers || {})
    }
  });

  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    // Le corps d'un refus porte parfois un drapeau qui dit a l'interface quoi
    // FAIRE, pas seulement quoi afficher : `requiresAccount` (POST /orders
    // refuse une session anonyme) doit conduire a l'inscription. En ne
    // gardant que le message, on affichait "Creez votre compte pour
    // commander" sans envoyer nulle part (signale le 2026-09-19).
    const erreur = new Error(payload?.error || 'Erreur API commandes.');
    erreur.status = response.status;
    erreur.requiresAccount = payload?.requiresAccount === true;
    erreur.payload = payload;
    throw erreur;
  }
  return payload;
};

// Lecture des commandes PAR L'API (chantier « hors Supabase ») : le serveur filtre sur le compte connecte et trie du plus
// recent au plus ancien, comme le faisait la lecture directe de la base.
export const listOrders = async () => {
  const data = await request('/orders');
  return Array.isArray(data) ? data : [];
};

export const listOrdersByBook = async (bookId) => {
  const data = await request(`/orders/book/${bookId}`);
  return Array.isArray(data) ? data : [];
};

export const getOrderById = async (orderId) => request(`/orders/${orderId}`);

export const createOrder = (payload) => request('/orders', {
  method: 'POST',
  body: JSON.stringify(payload)
});

// Supprime une commande pour repartir de zero (essais de formats, de types
// de commande, de paiement) — voir backend DELETE /api/orders/:orderId, qui
// supprime aussi les brouillons Gelato associes et refuse les seuls cas
// vraiment irreversibles (commande partie en production, ou payee avec
// Stripe en mode live).
export const deleteOrder = (orderId) => request(`/orders/${orderId}`, {
  method: 'DELETE'
});

// Change l'adresse d'une commande PAS ENCORE PAYEE : le serveur recalcule port, devise et total pour le nouveau pays et ferme
// la page de paiement Stripe deja ouverte (ancien montant). `billingAddress` null = identique a la livraison.
export const updateOrderAddress = (orderId, { shippingAddress, billingAddress }) => request(`/orders/${orderId}/address`, {
  method: 'PATCH',
  body: JSON.stringify({ shippingAddress, billingAddress })
});

// Ajoute (ou retire, code vide) un code promo sur une commande PAS ENCORE PAYEE : total recalcule par le serveur, page Stripe deja
// ouverte (ancien montant) fermee.
export const updateOrderPromo = (orderId, promoCode) => request(`/orders/${orderId}/promo`, {
  method: 'PATCH',
  body: JSON.stringify({ promoCode: promoCode || '' })
});

export const createStripeCheckoutSession = (orderId) => request(`/orders/${orderId}/checkout-session`, {
  method: 'POST'
});

export const confirmStripePayment = (orderId, sessionId) => request(`/orders/${orderId}/stripe/confirm`, {
  method: 'POST',
  body: JSON.stringify({ sessionId })
});

export const payOrder = (orderId, payload = {}) => request(`/orders/${orderId}/pay`, {
  method: 'POST',
  body: JSON.stringify(payload)
});

export const updateOrderStatus = (orderId, status, metadata = null) => request(`/orders/${orderId}/status`, {
  method: 'POST',
  body: JSON.stringify({
    status,
    metadata
  })
});

// Le mode production est-il actif ? Utilise pour adapter l'affichage cote
// client (ex. masquer "Supprimer cette commande et recommencer" en
// production — voir BookCheckoutLuxe.js). Portait jusqu'au 2026-10-04 aussi
// l'etat du bouton "Envoi de test a l'imprimeur", retire ce jour-la : en
// mode test, un paiement (meme factice) soumet deja un brouillon
// automatiquement, ce bouton etait devenu redondant.
export const getGelatoStatus = () => request('/orders/gelato/status');

// Suivi REEL de production : interroge l'imprimeur (voir
// backend/routes/orders.js GET /:orderId/tracking). Renvoie toujours
// quelque chose d'affichable, meme si Gelato est injoignable (`stale`).
export const getOrderTracking = (orderId) => request(`/orders/${orderId}/tracking`);

// Facture (voir backend/services/invoicing/invoiceService.js) : URL signee
// de courte duree, si elle existe deja. La generation part uniquement du
// paiement (jamais declenchee depuis le frontend) — 404 tant qu'elle n'est
// pas encore prete, a l'appelant de le distinguer d'une vraie erreur
// (voir InvoiceDownloadLink.js, error.status === 404).
export const getOrderInvoice = (orderId) => request(`/orders/${orderId}/invoice`);

export const getApiBaseUrl = buildApiBaseUrl;

// Catalogue des formats d'impression : dimensions reelles et prix de depart.
// PUBLIQUE (aucune session requise) : le format se choisit dans le parcours de
// creation, avant toute authentification. Le prix vient du serveur et non
// d'une table recopiee ici — il est calcule avec la meme formule que celui
// facture a la commande, donc les deux ne peuvent pas diverger.
//
// `pageCount` optionnel (chantier "tarification dynamique", 2026-09-27, §10) :
// quand un livre existe deja avec une vraie pagination, chaque format renvoie
// EN PLUS son prix a CETTE pagination (currentPriceCents) — un seul appel
// plutot que trois (un par format, comme le faisait BookPreviewFinalLuxe.js
// avant ce chantier).
export const listPrintFormats = async (pageCount) => {
  const query = Number.isFinite(pageCount) && pageCount > 0 ? `?page_count=${pageCount}` : '';
  // Le jeton est JOINT s'il existe (jamais exige) : un testeur recoit en plus les produits reserves a son
  // compte ; un visiteur ou un compte ordinaire recoit exactement la liste publique.
  let headers = {};
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers = { Authorization: `Bearer ${session.access_token}` };
  } catch (_error) { /* liste publique */ }
  const response = await fetchWithRetry(`${buildApiBaseUrl()}/orders/formats${query}`, { headers });
  if (!response.ok) throw new Error('Formats indisponibles');
  const data = await response.json();
  registerCatalogFormats(data?.formats);
  return data;
};

// Mode de paiement reel (vrai Stripe vs test) : PUBLIQUE, sans secret — sert
// uniquement a choisir le bon libelle de bouton avant la redirection vers
// Stripe (voir StepPayment.js). `live:false` en repli sur toute erreur
// reseau : jamais afficher "argent reel" par erreur si l'appel echoue.
export const getPaymentMode = async () => {
  try {
    const response = await fetchWithRetry(`${buildApiBaseUrl()}/orders/payment-mode`);
    if (!response.ok) return { live: false };
    return await response.json();
  } catch (_error) {
    return { live: false };
  }
};

// --- Emails transactionnels -------------------------------------------------
// `enabled` dit si une cle Resend est reellement posee cote serveur : il faut
// pouvoir l'afficher SANS envoyer d'email pour le savoir.
export const getEmailStatus = async () => {
  const response = await fetchWithRetry(`${buildApiBaseUrl()}/orders/email/status`, {
    headers: await buildHeaders()
  });
  if (!response.ok) throw new Error('Etat des emails indisponible');
  return response.json();
};

// Envoie un VRAI email d'essai, a l'adresse du compte connecte et a elle
// seule (le serveur n'accepte aucune adresse libre : ce serait un relais
// ouvert). Declenche explicitement par l'utilisateur.
export const sendTestEmail = async () => {
  const response = await fetchWithRetry(`${buildApiBaseUrl()}/orders/email/test`, {
    method: 'POST',
    headers: await buildHeaders()
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "L'envoi a echoue.");
  return data;
};
